import { makeAutoObservable, runInAction } from 'mobx';
import { Resource } from '../lib/types/resource';
import { Response } from '../lib/types/response';
import { isSuccess, toResource, toResourceError } from '../lib/utils/type-guards';
import { priceService } from '../services/price-service';
import { PriceData, CoinGeckoAssetData } from '../types';

export class PriceStore {
  // Resource for current price data
  private _currentPrices: Resource<PriceData> = {};
  
  // Loading state
  isLoadingPrices = false;

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Get current prices resource
   */
  get currentPrices(): Resource<PriceData> {
    return this._currentPrices;
  }

  /**
   * Get price for a specific asset
   */
  getPrice(assetId: string): number {
    if (!this._currentPrices || typeof this._currentPrices === 'object' && 'error' in this._currentPrices) {
      return 0;
    }
    return this._currentPrices[assetId] || 0;
  }

  /**
   * Check if we have price data for an asset
   */
  hasPrice(assetId: string): boolean {
    if (!this._currentPrices || typeof this._currentPrices === 'object' && 'error' in this._currentPrices) {
      return false;
    }
    return assetId in this._currentPrices;
  }

  /**
   * Fetch current prices for given asset IDs (now uses new cache system)
   */
  async fetchCurrentPrices(assetIds: string[]): Promise<Response<void>> {
    if (this.isLoadingPrices || assetIds.length === 0) {
      return undefined;
    }

    runInAction(() => {
      this.isLoadingPrices = true;
    });

    try {
      // Use the new caching getCurrentPrices method
      const response = await priceService.getCurrentPrices(assetIds);
      
      runInAction(() => {
        if (isSuccess(response)) {
          // Merge new prices with existing prices for the store state
          const existingPrices = this._currentPrices && typeof this._currentPrices === 'object' && !('error' in this._currentPrices) 
            ? this._currentPrices 
            : {};
          
          this._currentPrices = toResource({ ...existingPrices, ...response });
        } else {
          this._currentPrices = toResourceError(response);
        }
        this.isLoadingPrices = false;
      });

      return isSuccess(response) ? undefined : response;
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred');
      
      runInAction(() => {
        this._currentPrices = toResourceError(errorObj);
        this.isLoadingPrices = false;
      });
      
      return errorObj;
    }
  }

  /**
   * Get asset details (now uses new cache system)
   */
  async getAssetDetails(assetId: string): Promise<Response<CoinGeckoAssetData>> {
    // Delegate to the service which now handles caching
    return await priceService.getAssetDetails(assetId);
  }

  /**
   * Get cache statistics from the service
   */
  async getCacheStats() {
    return await priceService.getCacheStats();
  }

  /**
   * Clear all cached data (both store and service cache)
   */
  async clearCache(): Promise<Response<void>> {
    try {
      // Clear store state
      runInAction(() => {
        this._currentPrices = toResource({});
      });
      
      // Clear service cache
      const result = await priceService.clearCache();
      
      // Remove old localStorage cache for backward compatibility
      localStorage.removeItem('cgAssetCache');
      
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to clear cache';
      return new Error(`Cache clear failed: ${message}`);
    }
  }
}

export const priceStore = new PriceStore();
